import { module as BaseModule } from 'modujs';
import SplitType from 'split-type';
import gsap from 'gsap';

import { getVimeoId, getYouTubeId } from '../utils/video';

export default class extends BaseModule {
  constructor(m) {
    super(m);

    this.events = {
      click: {
        card: 'showProfileCard',
        profileVideo: 'showProfileVideo',
        quizTrigger: 'openQuiz',
      },
    };

    this.iObs = null;
    this.splittedHeading = null;
    this.heading = this.$('heading')[0];
    this.text = this.$('text')[0];
    this.modal = this.$('videoModal')[0];
    this.videoContainer = this.$('videoContainer')[0];
    this.closeModal = this.$('close')[0];
  }

  init() {
    this.initEvents();
    this.setupModalAnimation();
    this.setupIntersectionObserver();
    this.splitHeading();
  }

  initEvents() {
    // As this event is not cancellable,
    // just remove the class; cannot do the animation.
    this.modal.addEventListener('close', ev => {
      this.videoContainer.src = '';
    });

    // Close the modal when clicking the overlay
    this.modal.addEventListener('click', ev => {
      const dialogDimensions = this.modal.getBoundingClientRect();
      if (
        ev.target.nodeName === 'DIALOG' &&
        (
          ev.clientX < dialogDimensions.left ||
          ev.clientX > dialogDimensions.right ||
          ev.clientY < dialogDimensions.top ||
          ev.clientY > dialogDimensions.bottom
        )
      ) {
        this.modal.close();
      }
    });

    // Enable the close button
    this.closeModal.addEventListener('click', ev => {
      this.modal.close();
    })
  }

  setupModalAnimation() {
    const originalShowModal = this.modal.showModal;
    const originalClose = this.modal.close;

    this.modal.showModal = () => {
      originalShowModal.call(this.modal);
      this.modal.classList.add('is-open');
    };

    this.modal.close = () => {
      this.modal.addEventListener(
        'transitionend',
        ev => {
          originalClose.call(this.modal)
        },
        {once: true}
      );
      this.modal.classList.remove('is-open');
    }
  }

  setupIntersectionObserver() {
    if ( ! window.IntersectionObserver ) {
      return;
    }

    this.iObs = new IntersectionObserver(
      entries => this.handleIntersection( entries ),
      {
        threshold: [ 0.5 ],
      }
    );

    this.iObs.observe( this.heading );
    if ( window.matchMedia('(min-width: 60em)').matches ) {
      this.iObs.observe( this.el.querySelector('.c-profiles_inner') );
    } else {
      this.$('card').forEach( counter =>
        this.iObs.observe( counter )
      );
    }
  }

  splitHeading() {
    this.splittedHeading = new SplitType( this.heading, {
      types: 'lines',
      lineClass: 'o-line',
    });
  }

  handleIntersection( entries ) {
    entries.forEach( (entry, self) => {
      if ( entry.isIntersecting ) {
        if ( 'heading' === entry.target.dataset.profiles ) {
          this.animateText();
        } else if ( entry.target.classList.contains('c-profiles_inner') ) {
          this.animateCards( entry.target );
        } else if ( 'card' === entry.target.dataset.profiles ) {
          this.animateCard( entry.target );
        }
        this.iObs.unobserve( entry.target );
      }
    } );
  }

  animateText() {
    gsap.to( this.splittedHeading.lines, {
      duration: 0.8,
      opacity: 1,
      y: 0,
      stagger: 0.25,
      ease: 'power3.out',
      onComplete: () => {
        this.splittedHeading.revert();
      }
    } );

    gsap.to( this.text, {
      duration: 1,
      delay: 0.8,
      opacity: 1,
      ease: 'power3.out',
    } );
  }

  animateCards( element ) {
    gsap.to( element.querySelectorAll('.c-profiles_item'), {
      duration: 0.8,
      opacity: 1,
      y: 0,
      stagger: 0.1,
      ease: 'power3.out',
    } );
  }

  animateCard( card ) {
    gsap.to( card, {
      duration: 0.8,
      opacity: 1,
      y: 0,
      ease: 'power3.out',
    } );
  }

  showProfileCard(ev) {
    const current = this.$('card.is-active');

    if ( current.length && current[0] === ev.currentTarget ) {
      ev.currentTarget.classList.remove('is-active');
    } else {
      this.$('card.is-active').forEach((elem) => {
        elem.classList.remove('is-active');
      });
      ev.currentTarget.classList.add('is-active');
    }
  }

  showProfileVideo(ev) {
    const videoURL = ev.target.dataset.video;

    if ( videoURL.includes('youtube.com') ) {
      let youtube = getYouTubeId( videoURL );
      this.videoContainer.src = `https://www.youtube.com/embed/${youtube.videoId}?autoplay=1&color=white`;
    } else if ( videoURL.includes('vimeo.com') ) {
      let vimeo = getVimeoId( videoURL );
      if ( typeof vimeo.hash !== 'undefined' ) {
        this.videoContainer.src = `https://player.vimeo.com/video/${vimeo.videoId}?h=${vimeo.hash}&autoplay=1&title=0&byline=0&portrait=0`;
      } else {
        this.videoContainer.src = `https://player.vimeo.com/video/${vimeo.videoId}?autoplay=1&title=0&byline=0&portrait=0`;
      }
    }

    this.modal.showModal();
  }

  openQuiz(ev) {
    const card = this.parent('card', ev.currentTarget);

    this.call('open', card.dataset.profilesName, 'Quiz');
  }
}
